import React from 'react';
import FantasyMindLogo from './FantasyMindLogo';
import Button from './Button';

interface LandingPageProps {
  onSignIn?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignIn }) => {
  return (
    <div className="text-text-primary font-primary antialiased">
        {/* Hero Section */}
        <main 
          className="py-8 sm:py-12 md:py-20 relative"
          style={{
            backgroundImage: `url("./images/ff_background.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-bg-primary/70"></div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <div className="bg-bg-secondary/70 backdrop-blur-md p-10 rounded-2xl max-w-4xl mx-auto border border-border-primary">
              <h1 className="text-5xl md:text-6xl font-extrabold text-text-primary leading-tight mb-4">
                Dominate Your Fantasy League with{' '}
                <span className="bg-gradient-to-r from-accent-primary to-blue-400 bg-clip-text text-transparent">
                  AI-Powered Insights
                </span>
              </h1>
              <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto mb-8 font-secondary">
                Fantasy Mind is your secret weapon for making smarter decisions.
                Get expert advice on trades, waiver wire pickups, and lineup
                optimization.
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={onSignIn}
                  size="large"
                  className="rounded-full transition-transform transform hover:scale-105"
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        </main>

        {/* Features Section */}
        <section className="py-20 bg-bg-secondary" id="features">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-text-primary">
                All The Tools You Need To Win
              </h2>
              <p className="text-text-secondary mt-2 font-secondary">
                Our AI expert analyzes millions of data points to give you the edge.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-accent-primary mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-text-primary mb-3">AI Chat & Analysis</h3>
                <p className="text-text-secondary font-secondary">
                  Ask anything about your league. Get instant, data-driven advice on
                  player stats, trades, and waiver wire pickups.
                </p>
              </div>
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-accent-primary mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-text-primary mb-3">
                  Trade Analyzer
                </h3>
                <p className="text-text-secondary font-secondary">
                  Evaluate trade offers with our powerful analyzer. See if a trade
                  helps or hurts your team's chances of winning.
                </p>
              </div>
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-accent-primary mx-auto mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-text-primary mb-3">
                  Lineup Optimizer
                </h3>
                <p className="text-text-secondary font-secondary">
                  Set your best possible lineup each week. Our optimizer projects
                  player performance to maximize your points.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-bg-primary" id="pricing">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-text-primary">
                Choose Your Championship Plan
              </h2>
              <p className="text-text-secondary mt-2 font-secondary">
                Start free and upgrade as you dominate more leagues.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Free Plan */}
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200 border border-border-primary">
                <h3 className="text-2xl font-semibold text-text-primary mb-2">Rookie</h3>
                <div className="text-4xl font-bold text-text-primary mb-2">
                  $0
                  <span className="text-lg font-normal text-text-secondary">/month</span>
                </div>
                <p className="text-text-secondary mb-6 font-secondary">Perfect for getting started</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">1 league sync</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">League & team AI analysis</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-text-secondary font-secondary">AI Chat</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Lineup optimizer</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Trade analyzer</span>
                  </li>
                </ul>
                <Button variant="secondary" className="w-full" onClick={onSignIn}>
                  Start Free
                </Button>
              </div>

              {/* Pro Plan */}
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200 border-2 border-accent-primary relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-accent-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
                <h3 className="text-2xl font-semibold text-text-primary mb-2">Pro</h3>
                <div className="text-4xl font-bold text-text-primary mb-2">
                  $9.99
                  <span className="text-lg font-normal text-text-secondary">/month</span>
                </div>
                <p className="text-text-secondary mb-6 font-secondary">For serious competitors</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Up to 5 leagues</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">League & team AI analysis</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">AI Chat</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Lineup optimizer</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Trade analyzer</span>
                  </li>
                </ul>
                <Button className="w-full" onClick={onSignIn}>
                  Sign Up Now
                </Button>
              </div>

              {/* Champion Plan */}
              <div className="bg-bg-tertiary p-8 rounded-2xl text-center hover:bg-white/5 transition-all duration-200 border border-border-primary">
                <h3 className="text-2xl font-semibold text-text-primary mb-2">Champion</h3>
                <div className="text-4xl font-bold text-text-primary mb-2">
                  $19.99
                  <span className="text-lg font-normal text-text-secondary">/month</span>
                </div>
                <p className="text-text-secondary mb-6 font-secondary">For league champions</p>
                <ul className="text-left space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Unlimited leagues</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">League & team AI analysis</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">AI Chat (expanded context)</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Lineup optimizer</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-text-secondary font-secondary">Trade analyzer</span>
                  </li>
                </ul>
                <Button variant="secondary" className="w-full" onClick={onSignIn}>
                  Go Champion
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-bg-secondary border-t border-border-primary">
          <div className="container mx-auto px-6 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <FantasyMindLogo className="mb-4 md:mb-0" />
              <div className="flex space-x-6">
                <a className="text-text-secondary hover:text-text-primary transition-colors font-secondary" href="#">
                  Privacy Policy
                </a>
                <a className="text-text-secondary hover:text-text-primary transition-colors font-secondary" href="#">
                  Terms of Service
                </a>
                <a className="text-text-secondary hover:text-text-primary transition-colors font-secondary" href="#">
                  Contact
                </a>
              </div>
            </div>
            <div className="mt-8 border-t border-border-primary pt-6 text-center text-text-secondary text-sm font-secondary">
              © 2024 Fantasy Mind. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
  );
};

export default LandingPage;